import { useForm } from "react-hook-form";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { enqueueSnackbar } from "notistack";
import { yupResolver } from "@hookform/resolvers/yup";

import { IAvatarCreateFormType } from "./form-type";
import { avatarSchema } from "./schema";
import { createAvatarValidationSchema } from "./validation";

import { generateDefaultValueFromFormSchema } from "@/utils/generate-default-value-from-form-schema";
import ROUTE_CONSTANTS from "@/constants/route-constants";
import { getAxiosError } from "@/utils/get-axios-error";
import { avatarService } from "@/api/services/avatar";
import FormGenerator from "@/components/kit/FormGenerator";
import DashboardHeader from "@/components/app/DashboardHeader";
import DashboardContent from "@/components/app/DashboardContent";
import { useCheckPermissionAccess } from "@/hooks/useCheckPermissionAccess";
import { useCreateImage } from "@/api/hooks/useImage";
import { OperationAccess, PagesAccess } from "@/constants/enums/permissions";

const AvatarForm: React.FC = () => {
    const navigate = useNavigate();
    const [submitLoading, setSubmitLoading] = useState<boolean>(false);
    const listAccess = useCheckPermissionAccess(PagesAccess.AVATAR, OperationAccess.LIST);

    const { mutate: createImage } = useCreateImage();

    const {
        control,
        formState: { errors, isValid },
        handleSubmit,
    } = useForm<IAvatarCreateFormType>({
        defaultValues: generateDefaultValueFromFormSchema(avatarSchema),
        mode: "all",
        resolver: yupResolver(createAvatarValidationSchema),
    });

    const onSubmit = async (_val: IAvatarCreateFormType) => {
        try {
            setSubmitLoading(true);
            const formData = new FormData();

            formData.append("file", _val.avatar);
            formData.append("title", _val.title);
            createImage(
                { body: formData },
                {
                    onSuccess: async (res) => {
                        await avatarService.create({ mediaId: res.data.id, title: _val.title });
                        enqueueSnackbar(`Avatar created successfully`, { variant: "success" });
                        navigate(ROUTE_CONSTANTS.MEDIA_MANAGEMENT.AVATARS.ROOT.ABSOLUTE);
                    },
                }
            );
        } catch (err) {
            const error = getAxiosError(err);
            const message = error?.meta.message || "Server Error";

            enqueueSnackbar(message, { variant: "error" });
        } finally {
            setSubmitLoading(false);
        }
    };

    const footer = (
        <Stack direction={"row"} paddingBottom={10} spacing={2}>
            <LoadingButton disabled={!isValid || submitLoading} loading={submitLoading} type="submit" variant="contained">
                Create Avatar
            </LoadingButton>
            <Button type="button" onClick={() => navigate(-1)}>
                Cancel
            </Button>
        </Stack>
    );

    return (
        <>
            <DashboardHeader
                title="Create Avatar"
                {...(listAccess
                    ? {
                          titleSuffix: (
                              <Stack direction={"row"} spacing={2}>
                                  <Button component={Link} to={ROUTE_CONSTANTS.MEDIA_MANAGEMENT.AVATARS.ROOT.ABSOLUTE}>
                                      Back To Avatar List
                                  </Button>
                              </Stack>
                          ),
                      }
                    : undefined)}
            />
            <DashboardContent>
                <FormGenerator
                    control={control}
                    errors={errors}
                    footer={footer}
                    schema={avatarSchema}
                    onSubmit={handleSubmit(onSubmit)}
                />
            </DashboardContent>
        </>
    );
};

export default AvatarForm;
